'use strict';

/**
 * shared/publish/transitions.js — THE ONE canonical content-status state machine.
 *
 * WHY THIS EXISTS (publish-authority endgame, PA1)
 *   Every content-doc `status` flip in the product is governed by ONE legal-transition
 *   table. Before PA1 that table lived inline in `backend/src/services/PublishService.js`
 *   (backend-only), while the frontend ran its own hardcoded review-machine guards in
 *   `src/lib/constants.ts`. Two runtimes, two truths, drift guaranteed. This module is
 *   the single source both runtimes consume — exactly the house cross-runtime pattern of
 *   `shared/config/monitored-collections.js` and `shared/config/chains.js` (plain CJS,
 *   ZERO requires, importable by Node backend AND Next.js TS via `@shared/*`).
 *
 * PURITY CONTRACT
 *   Requires NOTHING (no firebase, no env, no clock). It is a pure data + predicate
 *   module: `isLegal` / `assertLegal` / `legalTargets` and the review-lane predicates
 *   are all referentially transparent. This keeps it classifiable as a pure test target
 *   in `backend/scripts/run-gates.js` and trivially importable from a browser bundle.
 *
 * THE MODEL
 *   `MACHINES[collection]` = { creation: [...to], states: { <from>: { to:[...], ...meta } } }.
 *   `'∅'` (CREATE) is the "no doc yet" from-state; `creation` lists the legal landing
 *   states of a brand-new doc. A state absent from `states` (or a `to` absent from a
 *   state's `to[]`) is ILLEGAL. Per-state metadata:
 *     - `terminal: true`       — no outgoing transitions (a sink; `beat`, `posted`).
 *     - `writerless: true`     — NOTHING writes this state; it is an accepted from-state
 *                                only (grep-verified: `in_review`). The PA9 monitor uses
 *                                this so it never expects a doc to ENTER the state.
 *     - `underReview: true`    — editor is actively reviewing (frontend review lane).
 *     - `editorPublishable: true` — an editor can approve→published straight from here.
 *   The flat `LEGAL_TRANSITIONS` map (backward-compatible with the old inline
 *   PublishService table) is DERIVED from MACHINES — there is only ONE authored table.
 *
 * AUDITABILITY
 *   Each collection block below is JSDoc-cited to the live writer(s) (file:line) that
 *   perform each transition, per the census- + code-verified spec §2
 *   (docs/superpowers/specs/2026-07-09-publish-authority-endgame-design.md) and the U5
 *   inventory (docs/ops/PUBLISH-STATE-MACHINE.md). The CODE is canon: rows were verified
 *   against the cited writers; where the spec's §2 draft disagreed with code, code won
 *   (corrections noted per-block).
 */

const CREATE = '∅'; // the "no doc yet" (creation) from-state key

/**
 * `breaking_news` — beats + external-coverage link items + suppressed beat stubs
 * (one collection). Enum (frontend canon): src/lib/constants.ts:39.
 * States: draft, submitted, in_review, needs_revision, published, rejected,
 *         rejected_by_verifier, superseded, beat, scheduled.
 *
 * Creation writers:
 *   ∅→published|draft|rejected_by_verifier  backend `storeDraft` B1
 *     (BreakingNewsDraftService.js:2895/:3023/:3042 — green auto-publish / hold / red).
 *   ∅→beat                                  HISTORICAL ONLY — fold-suppress `suppressAsBeat`
 *     retired CT1 2026-07-13; no live minter. Pre-CT1 `status:'beat'` docs remain valid.
 *   ∅→published|submitted                   backend external-coverage B7/B8
 *     (external-coverage.js:91/:133/:134 — first-party auto-approve vs gated-tier mint).
 *   ∅→draft                                 frontend create.
 *
 * Live transitions (writer per row):
 *   draft→scheduled          ops schedule-into-drip class (PUBLISH-STATE-MACHINE.md §1e).
 *   draft→published          backend B2 `retryCapHeldGreens` (:3212), B3 `approveDraft`
 *                            (:3435), backfill scripts; frontend `approveAndPublish`:1128
 *                            (validStatuses INCLUDES draft — AI-draft fast-approve).
 *   draft→rejected           backend B4 `rejectDraft` (:3752).
 *   draft→superseded         backend B5 `regenerate` (:4463).
 *   draft→submitted          frontend `submitForReview`:1064 (guard is a BLOCKLIST:
 *                            only published/submitted/in_review are refused).
 *   submitted→published      frontend `approveAndPublish`:1128.
 *   submitted→needs_revision frontend `requestRevision`:1297.
 *   submitted→rejected       frontend `rejectBreakingNews`:1381.
 *   submitted→draft          frontend `withdrawSubmission`:1471.
 *   in_review→(same exits as submitted) — `in_review` is WRITERLESS (no writer sets it,
 *                            grep-verified); modeled as an accepted from-state so historic
 *                            docs in it can still exit. Same review-lane exits as submitted.
 *   needs_revision→submitted frontend `submitForReview`:1064 (resubmit; blocklist admits it).
 *   scheduled→published      backend beat drip `publishDueBeats`→`approveBeat` (B3 wrapper).
 *   scheduled→draft          backend `maybeParkFailedBeat` (dead-letter park).
 *   published→draft          frontend `updateBreakingNews`:428 — admin correction lane;
 *                            DECIDED legal 2026-07-09 (expressible in prod today; the PA9
 *                            monitor reports usage). NOT modeled for articles/recaps.
 *   published→published      admin re-publish is idempotent in prod (B2 race /
 *                            `publishBreakingNews`:523 no-from-check); self-loop keeps
 *                            real-from-state validation from introducing a NEW throw.
 *   rejected→submitted            frontend `submitForReview`:1064 (resubmittable).
 *   rejected_by_verifier→submitted  frontend `submitForReview`:1064 (blocklist admits it).
 *   superseded→submitted          frontend `submitForReview`:1064 (blocklist admits it).
 *   beat→∅ (terminal).
 *
 * CORRECTION vs spec §2a draft: the spec listed `beat` and `scheduled` implicitly among
 * `submitForReview`'s blocklist-admitted from-states; `beat` is EXCLUDED here because
 * `submitForReview` also requires `content` (a beat is `content:''` → throws the content
 * precondition, so it is not actually admitted), and `scheduled`→submitted is excluded as
 * a non-canonical path (no real workflow un-schedules into review). Both exclusions are
 * status-safe: no writer performs them, so nothing new throws.
 *
 * ✅ PA5 AUDIT DONE (2026-07-09): `updateBreakingNews`
 * (src/lib/server/breaking-news-service-admin.ts:428) and `publishBreakingNews` (:523)
 * are now guarded by `assertLegal` (defense-in-depth BEHIND the existing user-facing
 * throws). The edit-form audit (BreakingNewsForm.tsx handleSaveDraft/handlePublish +
 * PATCH route) found three edit-reachable lanes the table lacked; they were ADDED above
 * (needs_revision→{published,draft}, rejected→draft) so enforcement doesn't break the
 * editor. `rejected→published` was CONFIRMED still-illegal (kept as the intended PA5
 * throw). `publishBreakingNews` is only ever called AFTER the form PUTs status:published
 * (updateBreakingNews/create) — so its live from-state is `published` (self-loop, legal,
 * zero new throws); a direct API hit on a non-published doc now throws (the point).
 */
const BREAKING_NEWS = {
  creation: ['published', 'draft', 'rejected_by_verifier', 'beat', 'submitted'],
  states: {
    draft: { to: ['scheduled', 'published', 'rejected', 'superseded', 'submitted'] },
    submitted: { to: ['published', 'needs_revision', 'rejected', 'draft'], underReview: true, editorPublishable: true },
    in_review: { to: ['published', 'needs_revision', 'rejected', 'draft'], underReview: true, editorPublishable: true, writerless: true },
    // needs_revision → published|draft: ADDED by PA5 (2026-07-09). The admin editor
    // (src/components/admin/BreakingNewsForm.tsx) shows an always-visible "Save Draft"
    // button (handleSaveDraft :253 → PUT status:draft) and, for canPublish users, a
    // "Publish" button (handlePublish :343 → PUT status:published) on a doc in ANY
    // status. Editing a `needs_revision` doc and clicking either drives
    // needs_revision→draft (save revision progress) or needs_revision→published (editor
    // fast-publish — parity with articles, whose `canPublishArticle` already admits
    // needs_revision→published). Both go through updateBreakingNews:428, so PA5 must
    // legalize them or assertLegal would be the FIRST (raw) error on a working editor
    // action. editorPublishable is set for parity with the articles machine (the derived
    // REVIEW_PUBLISHABLE set already contained needs_revision via articles — no change).
    needs_revision: { to: ['submitted', 'published', 'draft'], editorPublishable: true },
    scheduled: { to: ['published', 'draft'] },
    published: { to: ['draft', 'published'] },
    // rejected → draft: ADDED by PA5 (2026-07-09). The PATCH route
    // (src/app/api/breaking-news/[id]/route.ts:85) explicitly lists `rejected` among the
    // writer-editable statuses, and the editor's always-visible "Save Draft" button sends
    // status:draft — so revising a rejected item (rejected→draft) is a supported flow.
    // rejected → published is DELIBERATELY still ILLEGAL: reviving a spiked doc straight
    // to published is the intended-illegal flip PA5 catches (the backend census gate
    // asserts it throws — "resubmit lands in submitted"); the sanctioned revive path is
    // Save Draft (rejected→draft) or resubmit (rejected→submitted) then approve.
    rejected: { to: ['submitted', 'draft'] },
    // rejected_by_verifier|superseded → draft: ADDED by PA5 review fix (2026-07-09). The
    // PATCH route's `editableStatuses` gate (route.ts:85) applies ONLY to writers —
    // admins/owners (any doc) and editors (own docs) BYPASS it, so opening a superseded or
    // rejected_by_verifier doc by direct URL and clicking the always-visible "Save Draft"
    // button drives these transitions through updateBreakingNews:428 exactly like the
    // rejected→draft lane above (same button, same reachability, same revise rationale).
    // →published from BOTH stays ILLEGAL: the Publish button on a red-verdict or
    // superseded doc is the intended PA5 catch (census gate asserts both throw).
    rejected_by_verifier: { to: ['submitted', 'draft'] },
    superseded: { to: ['submitted', 'draft'] },
    beat: { to: [], terminal: true },
  },
};

/**
 * `articles` — editorial articles (article-writer skill / recap→article). Enum:
 * src/types/content.ts:21. States: draft, submitted, in_review, needs_revision,
 * scheduled, published, rejected, superseded.
 *
 * Creation writers:
 *   ∅→draft       article-writer (descout-article-from-briefing skill) / ops scripts.
 *   ∅→published   backend A3 `publishRecap(createArticle:true)` (EcosystemRecapService.js:1805).
 *
 * Live transitions (writer per row):
 *   draft→scheduled            approve route scheduling mode (api/articles/approve/route.ts:62
 *                              schedulableStatuses INCLUDES draft) + ops scripts.
 *   draft→published            backfill scripts (publish-drafts.js) — retained from the
 *                              pre-PA1 inline table (superset invariant: never de-legalize).
 *   draft→submitted            frontend `ContentReviewServiceBase.submitForReview`
 *                              (src/lib/content-review-service-base.ts:121, via
 *                              `articleReviewService` — src/lib/article-review-service.ts;
 *                              buttons: ArticleReviewActions.tsx:176 / useArticleReview.ts:167).
 *                              Guard is a BLOCKLIST: refuses only published + isUnderReview
 *                              {submitted,in_review} (shared/types/content-review.ts:139/:142).
 *   submitted→scheduled        approve route scheduling mode (:62).
 *   submitted→published        approve route publish mode `canPublishArticle` (:65).
 *   submitted→needs_revision   api/articles/request-revisions/route.ts:86 (`canReviewArticle`).
 *   submitted→rejected         api/articles/reject/route.ts:65 (`canReviewArticle`).
 *   submitted→draft            frontend `withdrawSubmission`
 *                              (content-review-service-base.ts:167, button
 *                              ArticleReviewActions.tsx:187) — requires isUnderReview.
 *   in_review→(same exits as submitted, incl. →draft withdraw) — WRITERLESS
 *                              (grep-verified), accepted from-state only.
 *   needs_revision→scheduled   approve route scheduling mode (:62 admits needs_revision).
 *   needs_revision→published   approve route publish mode `canPublishArticle` (:65 admits it).
 *   needs_revision→submitted   resubmit via the SAME submitForReview blocklist (:121;
 *                              census 2026-07-09: 4 live needs_revision docs).
 *   scheduled→published        backend A1 `publishDueArticles` (PublishSchedulerService.js:194).
 *   scheduled→draft            backend A2 story-liveness park (PublishSchedulerService.js:170).
 *   published→published        idempotent re-publish (self-loop).
 *   rejected→submitted         resubmit via the blocklist (:121; census: 6 live rejected docs).
 *
 * CORRECTION vs spec §2b draft (code is canon):
 *   - Spec draft said `draft|submitted|in_review → published (canPublishArticle)`. CODE:
 *     `canPublishArticle` === `canPublishContent` === {submitted, in_review, needs_revision}
 *     (src/lib/constants.ts:241/:254) — it does NOT include `draft` and DOES include
 *     `needs_revision`. So the approve-route publish lane is {submitted,in_review,
 *     needs_revision}→published; `draft→published` survives only via backfill scripts /
 *     the pre-PA1 table. `needs_revision→published` is ADDITIVE (new state, new legal row).
 *   - Spec omitted the `→scheduled` fan-in from submitted/in_review/needs_revision; the
 *     approve route's `schedulableStatuses` (:62) admits all four review-lane states, so
 *     those rows are encoded.
 *   - Spec omitted the article submit/withdraw writers entirely: `submitForReview`
 *     (content-review-service-base.ts:121) performs draft|needs_revision|rejected→submitted
 *     and `withdrawSubmission` (:167) performs submitted|in_review→draft — all encoded.
 *   - `scheduled→submitted` IS reachable via the submitForReview blocklist (scheduled is
 *     neither published nor under review) but is deliberately NOT modeled — non-canonical
 *     (mirrors the breaking_news exclusion: un-scheduling goes through the correction/park
 *     lanes, not review). ⚠ PA5: keep the service's existing user-facing guard-throws
 *     AHEAD of assertLegal on this path so assertLegal is defense-in-depth, never the
 *     first error a user sees.
 *   - `published→draft` is NOT legal for articles (unlike breaking_news): no writer does it
 *     and the pre-PA1 test asserts it throws.
 *
 * R1 corrections-desk ADDITION (2026-07-10):
 *   - `published→superseded` (ADDITIVE — the ONLY table row this unit adds). The corrections
 *     desk's SUPERSEDED flavor replaces a published article with a newer one: the old doc
 *     transitions published→superseded (leaves the live feed, keeps its URL + history — never
 *     deleted, record doctrine) and stamps `correction.supersededBy = <newId>`, executed by
 *     `PublishService.supersede()` through applyTransition. `superseded` is a TERMINAL sink
 *     for articles (no outgoing rows): a superseded article points forward to its replacement,
 *     which is the canonical live doc; reviving it is not modeled (add additively if ever
 *     needed). NOTE the asymmetry with breaking_news, where `superseded` is a *pre-publish*
 *     regenerate artifact (B5) that is resubmittable (superseded→{submitted,draft}); the
 *     articles sense is a *post-publish* supersession. The CORRECTED flavor (fix-in-place,
 *     status stays `published`) needs NO table row — it rides the published→published
 *     self-loop already legal here.
 */
const ARTICLES = {
  creation: ['draft', 'published'],
  states: {
    draft: { to: ['scheduled', 'published', 'submitted'] },
    submitted: { to: ['scheduled', 'published', 'needs_revision', 'rejected', 'draft'], underReview: true, editorPublishable: true },
    in_review: { to: ['scheduled', 'published', 'needs_revision', 'rejected', 'draft'], underReview: true, editorPublishable: true, writerless: true },
    needs_revision: { to: ['scheduled', 'published', 'submitted'], editorPublishable: true },
    scheduled: { to: ['published', 'draft'] },
    published: { to: ['published', 'superseded'] }, // +superseded: R1 corrections-desk supersede flavor (post-publish).
    rejected: { to: ['submitted'] },
    superseded: { to: [], terminal: true }, // R1 — post-publish supersession sink (points forward via correction.supersededBy).
  },
};

/**
 * `ecosystem_recaps` — daily/weekly recaps (EcosystemRecapService).
 * States: draft, scheduled, published.
 *
 * Creation writers:
 *   ∅→draft       R1 `generateDailyRecap`/weekly (needsReview default) — storeRecap :1708/:2239.
 *   ∅→published   R2 auto-publish-at-generation (EcosystemRecapService.js:382/:1973).
 * Live transitions:
 *   draft→scheduled     R4 the headless RECAP REVIEW lane — `ops.js schedule-headless <id>
 *                       --collection ecosystem_recaps`, actor `machine:headless-recap-scheduler`,
 *                       receipt-gated at the write (utils/headless-recap-review.recapScheduleGuard).
 *   draft→published     R3 `publishRecap` (EcosystemRecapService.js:1785) — the admin/human lane.
 *   scheduled→published R5 the recap drip `publishDueRecaps` (PublishSchedulerService), actor
 *                       `drip:recap`. ONE egress: never a raw status write.
 *   scheduled→draft     R6 the veto — `ops.js hold <id> --collection ecosystem_recaps`, and the
 *                       drip's own dead-letter park after MAX_DRIP_ATTEMPTS failures.
 *   published→published idempotent re-publish (self-loop; `publishRecap` is re-runnable in prod).
 *
 * WHY `scheduled` EXISTS HERE (2026-09-04, recap-process design §7 row 1). Until now the recap
 * lane's only machine publish hop was `backfill-publish` — silent and immediate — so a reviewer
 * could flag a bad recap but had no way to APPLY a verdict, and there was no window in which Lam
 * could pull one back. `scheduled` gives recaps the same veto window articles have: the reviewer
 * writes a receipt, the scheduler stamps a slot, the drip publishes at the slot, and `hold`
 * reverses it.
 *
 * WHERE A SCHEDULED RECAP IS VISIBLE, STATED EXACTLY. Every RENDERED reader — the recap pages,
 * the archive, the OG card, `/api/recaps`, `/api/ai/daily`, the RSS feeds, the chain hubs, the
 * Read/wire surfaces, the desk tape, the MCP tool, the vector preFilters — gates on
 * `status === 'published'`, and test-recap-scheduled-drip.js walks the shipped tree and fails on
 * any date-keyed reader that does not. The public JSON readers `GET /content/recap/recent` and
 * `GET /content/recap/:date` were NOT gated and now are (2026-09-04); the two Vercel
 * `/api/content/recap/{recent,trash}` routes were unauthenticated and now require editor auth.
 *
 * ONE DOOR IS STILL OPEN, and reading `firestore.rules` in this repo will NOT tell you so.
 * PROD still grants an unconditional read on `ecosystem_recaps`, so a client holding the public
 * Firebase config can read a draft or scheduled recap straight from Firestore, bypassing every
 * server filter above. The CLOSING DIFF IS IN THIS REPO AND NOT YET DEPLOYED — a rules deploy is
 * a human action (`firebase deploy --only firestore:rules --project eureka-meme`, Lam's) that
 * leaves no trace in the tree, so the committed file already reads as closed while prod is open.
 * The exposure predates this change (drafts have been readable for as long as drafts have
 * existed); what `scheduled` adds is an embargo break on top of it.
 *
 * Until that deploy lands: `scheduled` is hidden from every rendered surface and from the JSON
 * APIs, and is NOT a cryptographic embargo. backend/scripts/test-recap-scheduled-drip.js holds a
 * `RULES_DEPLOYED_ECOSYSTEM_RECAPS` flag the deployer flips, and requires this paragraph to keep
 * saying so until they do.
 *
 * `rejected`/`superseded` remain deliberately unmodeled: a recap is a dated digest — the DROP verb
 * for one is the soft-delete (`isDeleted`) the admin page already writes, and a stale recap is
 * regenerated, not superseded (the R1 corrections-desk `supersede()` is scoped to articles).
 */
const ECOSYSTEM_RECAPS = {
  creation: ['draft', 'published'],
  states: {
    draft: { to: ['scheduled', 'published'] },
    scheduled: { to: ['published', 'draft'] },
    published: { to: ['published'] },
  },
};

/**
 * `spotlights` — one per-calendar-day tweet-cadence doc (SpotlightService). A distinct
 * TWEET lifecycle, NOT an editorial publish; never merged with the editorial states.
 * States: posting, posted, skipped, skipped_degraded, failed.
 *
 * Writers (SpotlightService.js):
 *   ∅→posting                          S1 atomic slot claim (:178, `tx.set`).
 *   posting→posted                     S4 tweet succeeded (:1652); `twitterUrl` = permanent
 *                                      idempotency key. TERMINAL.
 *   posting→skipped                    S2 no candidates (:218).
 *   posting→skipped_degraded           S3 image render degraded (:1624).
 *   posting→failed                     S5 tweet failed (:1661).
 *   failed|skipped|skipped_degraded→posting   `force` admin re-claim (S1's real semantics:
 *                                      `_claimDecision` proceeds on force / expired claim,
 *                                      :380). Modeled so the PA9 monitor doesn't page on a
 *                                      legitimate force re-run.
 *
 * NOTE (code deviation deliberately NOT modeled): the force path in `_claimDecision` can
 * also re-claim an ALREADY-`posted` slot (twitterUrl set + force → proceed, :389) — the
 * rare admin override of a deleted tweet. Per spec §2d, `posted` is the terminal
 * idempotency state and `posted→posting` is NOT modeled (the pre-PA1 test asserts it
 * throws). Spotlight's claim does not route through `assertLegal` (S1 left as-is per U7),
 * so this exclusion introduces no new throw.
 */
const SPOTLIGHTS = {
  creation: ['posting'],
  states: {
    posting: { to: ['posted', 'skipped', 'skipped_degraded', 'failed'] },
    posted: { to: [], terminal: true },
    skipped: { to: ['posting'] },
    skipped_degraded: { to: ['posting'] },
    failed: { to: ['posting'] },
  },
};

/**
 * `tweet_queue` — the outbound tweet OUTBOX (TweetQueueService). Post lifecycle.
 * States: pending, posting, posted, failed.
 *
 * Writers (TweetQueueService.js):
 *   ∅→pending          `enqueueTweet` (:186) — a publish enqueues a pending outbox row.
 *   pending→posting     drain claim (:413) — `ContentOrchestrationScheduler.runTweetQueueDrain`.
 *   posting→posted      drain success. TERMINAL.
 *   posting→failed      drain failure / reaper.
 *   failed→pending      retry (ONLY if no `tweetUrl` — the PA3 reaper guard).
 */
const TWEET_QUEUE = {
  creation: ['pending'],
  states: {
    pending: { to: ['posting'] },
    posting: { to: ['posted', 'failed'] },
    posted: { to: [], terminal: true },
    failed: { to: ['pending'] },
  },
};

/** The one authored machine set. Everything else is derived from this. */
const MACHINES = {
  breaking_news: BREAKING_NEWS,
  articles: ARTICLES,
  ecosystem_recaps: ECOSYSTEM_RECAPS,
  spotlights: SPOTLIGHTS,
  tweet_queue: TWEET_QUEUE,
};

// ── Derived flat table (backward-compatible with the pre-PA1 inline PublishService copy).
// `{ collection: { '∅': [...creation], <from>: [...to] } }`. DERIVED, not authored — the
// grep invariant "exactly one legal-table definition" holds (this is the only `= {…machine}`).
const LEGAL_TRANSITIONS = {};
for (const [collection, machine] of Object.entries(MACHINES)) {
  const table = { [CREATE]: machine.creation.slice() };
  for (const [from, def] of Object.entries(machine.states)) {
    if (def.to && def.to.length) table[from] = def.to.slice();
  }
  LEGAL_TRANSITIONS[collection] = table;
}

// ── Editorial review lane — DERIVED from state metadata (single source for the
// src/lib/constants.ts guard helpers). Union across the two editorial collections; the
// review states carry identical semantics in breaking_news + articles.
function statesWithMeta(flag) {
  const set = new Set();
  for (const collection of ['breaking_news', 'articles']) {
    for (const [state, def] of Object.entries(MACHINES[collection].states)) {
      if (def[flag]) set.add(state);
    }
  }
  return set;
}
const UNDER_REVIEW = statesWithMeta('underReview');            // {submitted, in_review}
const REVIEW_PUBLISHABLE = statesWithMeta('editorPublishable'); // {submitted, in_review, needs_revision}

function fromKey(from) {
  return from === null || from === undefined ? CREATE : String(from);
}
function label(from) {
  return from === null || from === undefined ? CREATE : String(from);
}

/**
 * True iff `from → to` is a legal transition for `collection`.
 * `from == null` ⇒ creation (`∅`). Pure predicate; never throws.
 */
function isLegal(collection, from, to) {
  const table = LEGAL_TRANSITIONS[collection];
  if (!table) return false;
  const targets = table[fromKey(from)];
  return Array.isArray(targets) && targets.indexOf(to) !== -1;
}

/** Throw loudly (naming collection + from→to) unless the transition is legal. */
function assertLegal(collection, from, to) {
  if (!LEGAL_TRANSITIONS[collection]) {
    throw new Error(`[transitions] unknown collection "${collection}" — no legal transition table`);
  }
  if (!isLegal(collection, from, to)) {
    throw new Error(
      `[transitions] ILLEGAL transition for "${collection}": ${label(from)}→${to}` +
      ` (legal from ${label(from)}: ${JSON.stringify(LEGAL_TRANSITIONS[collection][fromKey(from)] || [])})`
    );
  }
  return true;
}

/**
 * The array of legal `to` states for `collection` from `from` (`from == null` ⇒ creation).
 * Returns a fresh array (never the internal one); `[]` for an unknown collection / terminal
 * / unmodeled from-state.
 */
function legalTargets(collection, from) {
  const table = LEGAL_TRANSITIONS[collection];
  if (!table) return [];
  const targets = table[fromKey(from)];
  return Array.isArray(targets) ? targets.slice() : [];
}

/** State metadata (`{terminal?, writerless?, underReview?, editorPublishable?}`) or `null`. */
function stateMeta(collection, state) {
  const machine = MACHINES[collection];
  if (!machine || !machine.states[state]) return null;
  const { to, ...meta } = machine.states[state]; // eslint-disable-line no-unused-vars
  return meta;
}

/** True iff `state` is terminal (no outgoing transitions) for `collection`. */
function isTerminal(collection, state) {
  return stateMeta(collection, state)?.terminal === true;
}

/** True iff `state` is writerless (nothing writes it; accepted from-state only). */
function isWriterless(collection, state) {
  return stateMeta(collection, state)?.writerless === true;
}

// ── Frontend review-lane predicates (single source for src/lib/constants.ts) ──────────
/** Content is actively under editorial review (submitted | in_review). */
function isUnderReview(status) {
  return UNDER_REVIEW.has(status);
}
/** An editor can approve→published straight from this status (submitted | in_review | needs_revision). */
function isReviewPublishable(status) {
  return REVIEW_PUBLISHABLE.has(status);
}

module.exports = {
  CREATE,
  MACHINES,
  LEGAL_TRANSITIONS,
  isLegal,
  assertLegal,
  legalTargets,
  stateMeta,
  isTerminal,
  isWriterless,
  isUnderReview,
  isReviewPublishable,
  // Exposed as arrays for the FE (Set is awkward to consume across the CJS/TS boundary in tests).
  UNDER_REVIEW: Array.from(UNDER_REVIEW),
  REVIEW_PUBLISHABLE: Array.from(REVIEW_PUBLISHABLE),
};
